import { apiFetch } from './client';
import type { AuthUser } from './auth';

export interface UserProfile extends AuthUser {
  hasPassword: boolean;
  updatedAt?: string;
}

export interface CompleteProfileResponse extends UserProfile {
  trialActivated: boolean;
}

export interface PlanDto {
  id: string;
  name: string;
  description?: string | null;
  value: number;
  qtrDiscount?: number;
  yearlyDiscount?: number;
  stripePriceId?: string | null;
  freeTrialDays: number;
  features: string[];
  popular?: boolean;
}

export interface UserPlanDto {
  planId: string;
  planName: string;
  price: number;
  recurrency: string;
  startedAt: string;
  expiresAt: string;
  daysUntilExpiry: number;
  expiringSoon: boolean;
  expired: boolean;
  isTrial: boolean;
}

export interface PlanQuotaCounter {
  used: number;
  limit: number | null;
}

export interface PlanUsageSnapshot {
  planName: string;
  unlimited: boolean;
  aiAnalysis: PlanQuotaCounter;
  favorites: PlanQuotaCounter;
  acquisitions: PlanQuotaCounter;
}

export interface UserPlanResponse {
  plan: UserPlanDto | null;
  usage: PlanUsageSnapshot;
}

export interface PlanUpsertPayload {
  name: string;
  description?: string;
  value: number;
  qtrDiscount?: number;
  yearlyDiscount?: number;
  stripePriceId?: string;
  freeTrialDays?: number;
  features?: string[];
  popular?: boolean;
}

export type InvoiceStatus = 'PENDING' | 'PAID' | 'CANCELLED';

export interface InvoiceDto {
  id: string;
  description: string;
  amount: number;
  status: InvoiceStatus;
  periodStart?: string | null;
  periodEnd?: string | null;
  paidAt?: string | null;
  createdAt: string;
}

export interface UpdateProfilePayload {
  name?: string;
  email?: string;
  phone?: string;
  cpf?: string;
  avatar?: string;
}

export const profileApi = {
  getProfile: () => apiFetch<UserProfile>('/users/me'),

  updateProfile: (data: UpdateProfilePayload) =>
    apiFetch<UserProfile>('/users/me', { method: 'PATCH', body: data }),

  completeProfile: (
    data: Required<Pick<UpdateProfilePayload, 'name' | 'phone' | 'cpf'>> &
      Pick<UpdateProfilePayload, 'avatar'>
  ) =>
    apiFetch<CompleteProfileResponse>('/users/me/complete-profile', { method: 'POST', body: data }),

  changePassword: (data: { currentPassword?: string; newPassword: string }) =>
    apiFetch<{ ok: boolean }>('/users/me/password', { method: 'PATCH', body: data }),

  listPlans: () => apiFetch<PlanDto[]>('/plans'),

  /** Catálogo para landing page e visitantes sem login */
  listPlansPublic: (opts?: { timeoutMs?: number }) =>
    apiFetch<PlanDto[]>('/plans', { skipAuth: true, timeoutMs: opts?.timeoutMs }),

  createPlan: (data: PlanUpsertPayload) =>
    apiFetch<PlanDto>('/plans', { method: 'POST', body: data }),

  updatePlan: (id: string, data: Partial<PlanUpsertPayload>) =>
    apiFetch<PlanDto>(`/plans/${id}`, { method: 'PATCH', body: data }),

  deletePlan: (id: string) => apiFetch<{ ok: boolean }>(`/plans/${id}`, { method: 'DELETE' }),

  getMyPlan: () => apiFetch<UserPlanResponse>('/users/me/plan'),

  changePlan: (planId: string) =>
    apiFetch<UserPlanDto>('/users/me/plan', { method: 'PATCH', body: { planId } }),

  listInvoices: () => apiFetch<InvoiceDto[]>('/users/me/invoices'),
};
